import { AUDIT_ACTION_LABELS, type AuditAction } from './audit.ts'
import { PROPOSAL_STATUS_LABELS, type ProposalStatus } from './catalog.ts'
import { SENDER_LABELS, type SenderLabelKey } from './chat.ts'
import {
  OFFER_STATUS_LABELS,
  type OfferStatus,
  ORDER_STATUS_LABELS,
  type OrderStatus,
  RFQ_STATUS_LABELS,
  type RfqStatus,
} from './deals.ts'
import type { Localized } from './i18n.ts'
import {
  ADMIN_LISTING_STATUS_LABELS,
  type AdminListingStatus,
  AVAILABILITY_LABELS,
  type AvailabilityMode,
  LISTING_STATUS_LABELS,
  type ListingStatus,
  STOCK_MOVEMENT_LABELS,
  STOCK_SIGNAL_LABELS,
  type StockMovementKind,
  type StockSignal,
} from './inventory.ts'
import {
  LEDGER_ACCOUNT_LABELS,
  type LedgerAccount,
  SETTLEMENT_METHOD_LABELS,
  type SettlementMethod,
} from './ledger.ts'
import {
  ALLOCATION_SOURCE_LABELS,
  type AllocationSource,
  PURCHASE_ORDER_STATUS_LABELS,
  type PurchaseOrderStatus,
  SUPPLY_ORDER_STATUS_LABELS,
} from './procurement.ts'
import { ORG_TYPE_LABELS, type OrgType, ROLE_LABELS, type Role } from './rbac.ts'
import { REPORT_RANGE_LABELS, type ReportRange } from './reports.ts'
import { INVITE_STATUS_LABELS, type InviteStatus } from './sourcing.ts'

/**
 * Every enum that reaches a screen, in both languages.
 *
 * The English maps stay where they are — beside the enum they describe, and used
 * directly by the server for audit summaries and log lines. This module only adds
 * the second column and puts both behind one lookup, so a screen asks for
 * `LABELS[locale].orderStatus[status]` instead of choosing a map itself.
 *
 * `satisfies Localized<LabelSets>` is the guard that matters: adding a status to
 * any enum fails the build here until the Nepali word exists, which is what stops
 * the half-translated screen this feature was deferred over.
 *
 * On the Nepali copy: this is the vocabulary of the trade rather than of the
 * dictionary — अर्डर, स्टक, रेट, पसल — because that is what a hardware shop says out
 * loud, and a word the trade already says in English is written in Devanagari
 * rather than translated (क्याटेगोरी, not वर्ग). See apps/web/src/i18n/ne.ts for the
 * full rule. Numerals stay Arabic throughout (see i18n.ts).
 */
export interface LabelSets {
  role: Record<Role, string>
  orgType: Record<OrgType, string>
  proposalStatus: Record<ProposalStatus, string>
  listingStatus: Record<ListingStatus, string>
  adminListingStatus: Record<AdminListingStatus, string>
  availability: Record<AvailabilityMode, string>
  stockSignal: Record<StockSignal, string>
  stockMovement: Record<StockMovementKind, string>
  rfqStatus: Record<RfqStatus, string>
  offerStatus: Record<OfferStatus, string>
  orderStatus: Record<OrderStatus, string>
  purchaseOrderStatus: Record<PurchaseOrderStatus, string>
  supplyOrderStatus: Record<PurchaseOrderStatus, string>
  allocationSource: Record<AllocationSource, string>
  inviteStatus: Record<InviteStatus, string>
  ledgerAccount: Record<LedgerAccount, string>
  settlementMethod: Record<SettlementMethod, string>
  reportRange: Record<ReportRange, string>
  auditAction: Record<AuditAction, string>
  sender: Record<SenderLabelKey, string>
}

const en: LabelSets = {
  role: ROLE_LABELS,
  orgType: ORG_TYPE_LABELS,
  proposalStatus: PROPOSAL_STATUS_LABELS,
  listingStatus: LISTING_STATUS_LABELS,
  adminListingStatus: ADMIN_LISTING_STATUS_LABELS,
  availability: AVAILABILITY_LABELS,
  stockSignal: STOCK_SIGNAL_LABELS,
  stockMovement: STOCK_MOVEMENT_LABELS,
  rfqStatus: RFQ_STATUS_LABELS,
  offerStatus: OFFER_STATUS_LABELS,
  orderStatus: ORDER_STATUS_LABELS,
  purchaseOrderStatus: PURCHASE_ORDER_STATUS_LABELS,
  supplyOrderStatus: SUPPLY_ORDER_STATUS_LABELS,
  allocationSource: ALLOCATION_SOURCE_LABELS,
  inviteStatus: INVITE_STATUS_LABELS,
  ledgerAccount: LEDGER_ACCOUNT_LABELS,
  settlementMethod: SETTLEMENT_METHOD_LABELS,
  reportRange: REPORT_RANGE_LABELS,
  auditAction: AUDIT_ACTION_LABELS,
  sender: SENDER_LABELS,
}

const ne: LabelSets = {
  role: {
    super_admin: 'मालिक',
    admin: 'स्टाफ',
    seller: 'पसल',
    buyer: 'ग्राहक',
  },
  orgType: {
    platform: 'प्लेटफर्म',
    seller: 'सप्लायर पसल',
    buyer: 'ग्राहक',
  },
  proposalStatus: {
    pending: 'हेर्न बाँकी',
    approved: 'लिस्टमा थपियो',
    rejected: 'थपिएन',
    merged: 'पुरानै सामानसँग मिलाइयो',
  },
  listingStatus: {
    active: 'बिक्रीमा',
    paused: 'रोकिएको',
    archived: 'हटाइएको',
  },
  adminListingStatus: {
    draft: 'ड्राफ्ट',
    published: 'ग्राहकलाई देखिने',
    unpublished: 'लुकाइएको',
    archived: 'हटाइएको',
  },
  availability: {
    platform_stock: 'हाम्रो स्टकबाट',
    seller_stock: 'सप्लायर पसलबाट',
    made_to_order: 'अर्डर गरेर ल्याइने',
  },
  stockSignal: {
    in_stock: 'तयार छ',
    limited: 'थोरै मात्र',
    to_order: 'अर्डर गरेर ल्याइने',
  },
  stockMovement: {
    receive: 'माल भित्रियो',
    reserve: 'अर्डरका लागि छुट्याइयो',
    release: 'फेरि स्टकमा राखियो',
    issue: 'पठाइयो',
    adjust: 'गन्ती मिलाइयो',
    return: 'फिर्ता आयो',
  },
  rfqStatus: {
    open: 'रेट आउन बाँकी',
    quoted: 'रेट पठाइयो',
    negotiating: 'कुरा चलिरहेको',
    accepted: 'मिल्यो',
    declined: 'अस्वीकार भयो',
    expired: 'समय सकियो',
    cancelled: 'रद्द भयो',
  },
  offerStatus: {
    sent: 'जवाफ आउन बाँकी',
    accepted: 'मिल्यो',
    rejected: 'अस्वीकार भयो',
    superseded: 'नयाँ रेटले फेरियो',
    withdrawn: 'फिर्ता लिइयो',
    expired: 'समय सकियो',
  },
  orderStatus: {
    confirmed: 'पक्का भयो',
    packing: 'तयार हुँदै',
    dispatched: 'बाटोमा',
    delivered: 'पुग्यो',
    cancelled: 'रद्द भयो',
  },
  purchaseOrderStatus: {
    sent: 'पसलको जवाफ पर्खिँदै',
    confirmed: 'स्वीकार भयो',
    declined: 'अस्वीकार भयो',
    received: 'माल भित्रियो',
    cancelled: 'रद्द भयो',
  },
  supplyOrderStatus: {
    sent: 'तपाईंको जवाफ चाहियो',
    confirmed: 'तपाईंले स्वीकार गर्नुभयो',
    declined: 'तपाईंले अस्वीकार गर्नुभयो',
    received: 'पुर्‍याइयो',
    cancelled: 'डेस्कले रद्द गर्‍यो',
  },
  allocationSource: {
    platform_stock: 'हाम्रै स्टक',
    purchase_order: 'पसलबाट',
  },
  inviteStatus: {
    invited: 'पर्खिँदै',
    quoted: 'रेट आयो',
    declined: 'अस्वीकार भयो',
    expired: 'जवाफ आएन',
  },
  ledgerAccount: {
    buyer_receivable: 'ग्राहकले तिर्न बाँकी',
    seller_payable: 'पसललाई तिर्न बाँकी',
    sales: 'बिक्री',
    purchases: 'मालको लागत',
    cash: 'नगद',
  },
  settlementMethod: {
    cash: 'नगद',
    bank: 'बैंक ट्रान्सफर',
    cheque: 'चेक',
    adjustment: 'मिनाहा वा मिलान',
  },
  reportRange: {
    '7d': 'पछिल्ला 7 दिन',
    '30d': 'पछिल्ला 30 दिन',
    '90d': 'पछिल्ला 90 दिन',
  },
  auditAction: {
    'auth.login': 'साइन इन गर्नुभयो',
    'auth.login_failed': 'साइन इन असफल भयो',
    'auth.logout': 'साइन आउट गर्नुभयो',
    'auth.password_changed': 'पासवर्ड फेर्नुभयो',
    'auth.session_revoked': 'सेसन बन्द भयो',
    'org.created': 'व्यवसाय थप्नुभयो',
    'org.updated': 'व्यवसाय एडिट गर्नुभयो',
    'org.status_changed': 'व्यवसायको स्थिति फेर्नुभयो',
    'user.created': 'प्रयोगकर्ता बनाउनुभयो',
    'user.updated': 'प्रयोगकर्ता एडिट गर्नुभयो',
    'user.status_changed': 'प्रयोगकर्ताको स्थिति फेर्नुभयो',
    'user.password_reset': 'पासवर्ड रिसेट गर्नुभयो',
    'category.created': 'क्याटेगोरी थप्नुभयो',
    'category.updated': 'क्याटेगोरी एडिट गर्नुभयो',
    'category.deleted': 'क्याटेगोरी हटाउनुभयो',
    'product.created': 'सामान थप्नुभयो',
    'product.updated': 'सामान एडिट गर्नुभयो',
    'product.status_changed': 'सामानको स्थिति फेर्नुभयो',
    'product.merged': 'दोहोरिएका सामान गाभ्नुभयो',
    'product_proposal.created': 'नयाँ सामान सुझाउनुभयो',
    'product_proposal.approved': 'सुझाइएको सामान स्वीकार गर्नुभयो',
    'product_proposal.rejected': 'सुझाइएको सामान अस्वीकार गर्नुभयो',
    'product_proposal.merged': 'सुझावलाई पुरानै सामानसँग मिलाउनुभयो',
    'listing.created': 'पसलको सामान थप्नुभयो',
    'listing.updated': 'पसलको सामान एडिट गर्नुभयो',
    'listing.status_changed': 'पसलको सामानको स्थिति फेर्नुभयो',
    'listing.tiers_changed': 'थोक रेट फेर्नुभयो',
    'listing.imported': 'रेट लिस्ट अपलोड गर्नुभयो',
    'admin_listing.created': 'लिस्टको सामान थप्नुभयो',
    'admin_listing.updated': 'लिस्टको सामान एडिट गर्नुभयो',
    'admin_listing.published': 'ग्राहकलाई देखाउनुभयो',
    'admin_listing.unpublished': 'ग्राहकबाट लुकाउनुभयो',
    'admin_listing.tiers_changed': 'ग्राहकको थोक रेट फेर्नुभयो',
    'stock.adjusted': 'स्टक मिलाउनुभयो',
    'stock.received': 'स्टक भित्र्याउनुभयो',
    'stock.reserved': 'स्टक छुट्याउनुभयो',
    'stock.released': 'छुट्याइएको स्टक फुकाउनुभयो',
    'stock.issued': 'स्टक पठाउनुभयो',
    'stock.returned': 'स्टक फिर्ता लिनुभयो',
    'rfq.created': 'रेट माग्नुभयो',
    'rfq.assigned': 'रेट अनुरोध जिम्मा दिनुभयो',
    'rfq.cancelled': 'रेट अनुरोध रद्द गर्नुभयो',
    'rfq.closed': 'रेट अनुरोध बन्द गर्नुभयो',
    'offer.sent': 'रेट पठाउनुभयो',
    'offer.countered': 'अर्को रेट सुझाउनुभयो',
    'offer.accepted': 'रेट स्वीकार गर्नुभयो',
    'offer.rejected': 'रेट अस्वीकार गर्नुभयो',
    'offer.withdrawn': 'रेट फिर्ता लिनुभयो',
    'offer.expired': 'रेटको समय सकियो',
    'order.created': 'अर्डर बनाउनुभयो',
    'order.status_changed': 'अर्डर अगाडि बढाउनुभयो',
    'order.cancelled': 'अर्डर रद्द गर्नुभयो',
    'sourcing.created': 'पसलहरूसँग रेट बुझ्न सुरु गर्नुभयो',
    'sourcing.invited': 'पसललाई रेट सोध्नुभयो',
    'sourcing.quoted': 'पसलले रेट पठायो',
    'sourcing.declined': 'पसलले रेट दिन मानेन',
    'sourcing.closed': 'रेट बुझ्ने काम सिध्याउनुभयो',
    'purchase_order.created': 'पसललाई अर्डर गर्नुभयो',
    'purchase_order.confirmed': 'पसलले अर्डर स्वीकार गर्‍यो',
    'purchase_order.declined': 'पसलले अर्डर अस्वीकार गर्‍यो',
    'purchase_order.received': 'पसलबाट माल भित्रियो',
    'purchase_order.cancelled': 'पसललाई गरेको अर्डर रद्द गर्नुभयो',
    'order.allocated': 'अर्डर कहाँबाट पूरा गर्ने छान्नुभयो',
    'order.unallocated': 'सप्लाइको स्रोत हटाउनुभयो',
    'ledger.settled': 'भुक्तानी दर्ता गर्नुभयो',
    'price_book.created': 'रेट मिलापत्र बनाउनुभयो',
    'price_book.updated': 'रेट मिलापत्र फेर्नुभयो',
    'price_book.assigned': 'रेट मिलापत्रमा को छ भन्ने फेर्नुभयो',
    'conversation.created': 'कुराकानी सुरु गर्नुभयो',
    'conversation.assigned': 'कुराकानी जिम्मा दिनुभयो',
    'conversation.closed': 'कुराकानी बन्द गर्नुभयो',
    'settings.updated': 'सेटिङ फेर्नुभयो',
  },
  sender: {
    update: 'अपडेट',
    you: 'तपाईं',
    desk: 'सपोर्ट डेस्क',
    customer: 'ग्राहक',
  },
}

export const LABELS: Localized<LabelSets> = { en, ne }
